import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog } from '@headlessui/react';
import {
  XMarkIcon,
  UserGroupIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Event, EventType, AttendanceStatus } from '../../types/event';
import {
  Match,
  MatchCreateDto,
  MatchIncidentType,
  MatchSelectionRole,
  MatchPlayerResultDto,
  MatchOutcome,
  SelectablePlayer,
  getMatchOutcome,
} from '../../types/match';
import { eventsAPI, matchesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isStaff } from '../../types/auth';
import { useBatchedList } from '../../hooks/useBatchedList';
import LazyLoadTrigger from '../ui/LazyLoadTrigger';
import FormationPitch, { Jersey, PitchPlayer } from './FormationPitch';
import LineupEditor from './LineupEditor';
import { matchFormation, slotsFromLineup } from './formations';
import { RESPONSE_BADGES } from './responseBadges';
import { ShirtSuggestionMap, fillShirts, suggestShirt, toSuggestionMap } from './shirtSuggestions';
import AiButton from '../ai/AiButton';
import AiMatchSummaryDialog from '../ai/AiMatchSummaryDialog';
import HelpHint from '../help/HelpHint';
import { useAiStatus } from '../../hooks/useAiStatus';

const OUTCOME_LABELS: Record<MatchOutcome, { text: string; className: string }> = {
  win: { text: 'Győzelem', className: 'text-green-600' },
  draw: { text: 'Döntetlen', className: 'text-yellow-600' },
  loss: { text: 'Vereség', className: 'text-red-600' },
};

type TabId = 'nevezes' | 'eredmeny' | 'stat';

const TAB_LABELS: Record<TabId, string> = {
  nevezes: 'Nevezés',
  eredmeny: 'Eredmény',
  stat: 'Statisztika',
};

interface MatchDetailViewProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onEditEvent?: () => void;
}

const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
};

interface PlayerIncidentCounts {
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

// Játékosonkénti darabszámok az eseményekből. A gólpassz kétféleképp lehet rögzítve:
// külön Gólpassz bejegyzésként, vagy a gól "gólpassz adója"-ként – mindkettő számít.
const countIncidents = (match: Match): Map<number, PlayerIncidentCounts> => {
  const counts = new Map<number, PlayerIncidentCounts>();
  const get = (playerId: number) => {
    let c = counts.get(playerId);
    if (!c) {
      c = { goals: 0, assists: 0, yellowCards: 0, redCards: 0 };
      counts.set(playerId, c);
    }
    return c;
  };
  match.incidents.forEach((i) => {
    switch (i.type) {
      case MatchIncidentType.Goal:
        get(i.playerId).goals++;
        if (i.secondaryPlayerId != null) get(i.secondaryPlayerId).assists++;
        break;
      case MatchIncidentType.Assist:
        get(i.playerId).assists++;
        break;
      case MatchIncidentType.YellowCard:
        get(i.playerId).yellowCards++;
        break;
      case MatchIncidentType.RedCard:
        get(i.playerId).redCards++;
        break;
    }
  });
  return counts;
};

const MatchDetailView: React.FC<MatchDetailViewProps> = ({ event, isOpen, onClose, onEditEvent }) => {
  const { user } = useAuth();
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('nevezes');
  const [selectablePlayers, setSelectablePlayers] = useState<SelectablePlayer[]>([]);
  const [responses, setResponses] = useState<Map<number, AttendanceStatus>>(new Map());
  const [shirtSuggestions, setShirtSuggestions] = useState<ShirtSuggestionMap>(new Map());

  const canEdit = isStaff(user);
  const ai = useAiStatus();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const isMatchEvent = event?.type === EventType.Match;
  const [futsalSaving, setFutsalSaving] = useState(false);

  const toggleFutsal = async (isFutsal: boolean) => {
    if (!match) return;
    setFutsalSaving(true);
    try {
      setMatch(await matchesAPI.update(match.id, { isFutsal }));
    } catch (err) {
      console.error('Futsal toggle error:', err);
    } finally {
      setFutsalSaving(false);
    }
  };

  const eventId = event?.id;
  const eventStart = event?.startTime;

  const loadMatch = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await eventsAPI.getMatch(eventId);
      setMatch(data);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err && (err as { response?: { status?: number } }).response?.status === 404) {
        // Régebbi mérkőzés eseményhez még nincs rekord — a háttérben létrehozzuk,
        // külön "Mérkőzés létrehozása" lépés nélkül
        if (isStaff(user)) {
          try {
            const created = await eventsAPI.createMatch(eventId, {} as MatchCreateDto);
            setMatch(created);
          } catch (createErr) {
            console.error(createErr);
            setMatch(null);
          }
        } else {
          setMatch(null);
        }
      } else {
        setError('Hiba a mérkőzés betöltésekor');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, user]);

  // A nevezéshez a stáb a teljes aktív játékoskeretet és a visszajelzéseket látja
  const loadSelectionData = useCallback(async () => {
    if (!eventId || !canEdit) return;
    try {
      const [players, eventResponses] = await Promise.all([
        matchesAPI.getSelectablePlayers(),
        eventsAPI.getResponses(eventId).catch(() => []),
      ]);
      setSelectablePlayers(players);
      const map = new Map<number, AttendanceStatus>();
      eventResponses.forEach((r) => {
        if (r.playerId != null) map.set(r.playerId, r.status);
      });
      setResponses(map);
    } catch (err) {
      console.error('Players load error:', err);
    }
  }, [eventId, canEdit]);

  useEffect(() => {
    if (isOpen && eventId && isMatchEvent) {
      // Lejátszott meccsnél az eredmény rögzítése a gyakoribb feladat
      const started = eventStart ? new Date(eventStart).getTime() < Date.now() : false;
      setActiveTab(started ? 'eredmeny' : 'nevezes');
      loadMatch();
      loadSelectionData();
    }
  }, [isOpen, eventId, isMatchEvent, eventStart, loadMatch, loadSelectionData]);

  // Mezszám-javaslatok a korosztály korábbi meccseiből (csak a stábnak)
  const matchId = match?.id;
  useEffect(() => {
    if (!matchId || !canEdit) return;
    matchesAPI
      .getShirtSuggestions(matchId)
      .then((list) => setShirtSuggestions(toSuggestionMap(list)))
      .catch((err) => console.error('Shirt suggestions load error:', err));
  }, [matchId, canEdit]);

  if (!event || !isMatchEvent) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile bg-white shadow-xl w-full overflow-hidden flex flex-col md:rounded-lg md:max-w-4xl md:max-h-[90vh]">
          {summaryOpen && <AiMatchSummaryDialog eventId={event.id} isOpen onClose={() => setSummaryOpen(false)} />}
          <div className="flex items-center justify-between p-4 border-b shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <UserGroupIcon className="h-6 w-6 text-red-600 shrink-0" />
              <Dialog.Title className="text-xl font-bold text-gray-900 truncate">
                Mérkőzés: {event.title}
              </Dialog.Title>
              {match?.isFutsal && !canEdit && (
                <span className="shrink-0 rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">Futsal</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {match && canEdit && ai.available && (
                <span className="inline-flex items-center gap-1">
                  <AiButton variant="link" onClick={() => setSummaryOpen(true)} title="Mérkőzés-beszámoló AI-val" className="px-2 py-1">
                    <span className="hidden sm:inline">Beszámoló</span>
                  </AiButton>
                  <HelpHint id="matches.aiSummary" />
                </span>
              )}
              {match && canEdit && (
                <label
                  className="flex items-center gap-1.5 rounded-full px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
                  title="Futsal mérkőzésnél a felállás 4+1 fős"
                >
                  <input
                    type="checkbox"
                    checked={!!match.isFutsal}
                    disabled={futsalSaving}
                    onChange={(e) => toggleFutsal(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Futsal
                </label>
              )}
              {onEditEvent && canEdit && (
                <button
                  type="button"
                  onClick={onEditEvent}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                  title="Esemény szerkesztése"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center flex-1 p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
          ) : error ? (
            <div className="p-6 text-red-600 cursor-pointer" onClick={() => setError(null)}>{error}</div>
          ) : !match ? (
            <div className="p-6">
              <p className="text-gray-600">Ehhez az eseményhez még nincs mérkőzés adat.</p>
            </div>
          ) : (
            <>
              <div className="flex border-b px-4 gap-1 shrink-0">
                {(['nevezes', 'eredmeny', 'stat'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                      activeTab === tab
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {TAB_LABELS[tab]}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6 pb-safe-6">
                {activeTab === 'nevezes' && (
                  <MatchSelectionTab
                    match={match}
                    event={event}
                    canEdit={canEdit}
                    selectablePlayers={selectablePlayers}
                    responses={responses}
                    shirtSuggestions={shirtSuggestions}
                    onSaved={setMatch}
                  />
                )}
                {activeTab === 'eredmeny' && (
                  <MatchResultTab
                    match={match}
                    event={event}
                    canEdit={canEdit}
                    onSaved={setMatch}
                    onGoToSelection={() => setActiveTab('nevezes')}
                  />
                )}
                {activeTab === 'stat' && (
                  <MatchStatsTab match={match} event={event} />
                )}
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

// --- Nevezés tab: ki kezd, ki csere, mezszám, poszt + meccs előtti jegyzetek ---
interface SelectionEntry {
  role: MatchSelectionRole;
  shirtNumber?: number;
  position?: string;
  // A felállás-ábrán elfoglalt hely; a listás szerkesztés megőrzi
  pitchX?: number;
  pitchY?: number;
}

const buildSelectionState = (match: Match): Record<number, SelectionEntry> => {
  const state: Record<number, SelectionEntry> = {};
  // A régi adatoknál előfordulhat, hogy valaki csak a keretben szerepel – őt cserének vesszük
  match.squad.forEach((s) => {
    state[s.playerId] = { role: MatchSelectionRole.Sub, shirtNumber: s.shirtNumber };
  });
  match.subs.forEach((s) => {
    state[s.playerId] = {
      role: MatchSelectionRole.Sub,
      shirtNumber: s.shirtNumber ?? state[s.playerId]?.shirtNumber,
      position: s.position || undefined,
    };
  });
  match.lineup.forEach((l) => {
    state[l.playerId] = {
      role: MatchSelectionRole.Starter,
      shirtNumber: l.shirtNumber ?? state[l.playerId]?.shirtNumber,
      position: l.position || undefined,
      pitchX: l.pitchX ?? undefined,
      pitchY: l.pitchY ?? undefined,
    };
  });
  return state;
};

// Sorrendfüggetlen összehasonlítás: ki-be kapcsolás után ne jelezzen hamis változást
const selectionKey = (state: Record<number, SelectionEntry>): string =>
  JSON.stringify(
    Object.keys(state)
      .map(Number)
      .sort((a, b) => a - b)
      .map((id) => [id, state[id].role, state[id].shirtNumber ?? null, state[id].position || null])
  );

interface NotesState {
  opponentName: string;
  opponentNotes: string;
  setPieceAttackNotes: string;
  setPieceDefenseNotes: string;
  otherNotes: string;
}

const buildNotesState = (match: Match): NotesState => ({
  opponentName: match.opponentName ?? '',
  opponentNotes: match.opponentNotes ?? '',
  setPieceAttackNotes: match.setPieceAttackNotes ?? '',
  setPieceDefenseNotes: match.setPieceDefenseNotes ?? '',
  otherNotes: match.otherNotes ?? '',
});

interface SelectionRowPlayer {
  id: number;
  name: string;
  otherAgeGroup?: string;
}

interface MatchSelectionTabProps {
  match: Match;
  event: Event;
  canEdit: boolean;
  selectablePlayers: SelectablePlayer[];
  responses: Map<number, AttendanceStatus>;
  shirtSuggestions: ShirtSuggestionMap;
  onSaved: (match: Match) => void;
}

const MatchSelectionTab: React.FC<MatchSelectionTabProps> = ({
  match,
  event,
  canEdit,
  selectablePlayers,
  responses,
  shirtSuggestions,
  onSaved,
}) => {
  const [selection, setSelection] = useState<Record<number, SelectionEntry>>(() => buildSelectionState(match));
  const [notes, setNotes] = useState<NotesState>(() => buildNotesState(match));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Mentett nevezésnél először az összesítő látszik, szerkeszteni gombbal lehet
  const [editing, setEditing] = useState(false);
  const [lineupEditorOpen, setLineupEditorOpen] = useState(false);

  useEffect(() => {
    setSelection(buildSelectionState(match));
    setNotes(buildNotesState(match));
  }, [match]);

  const initialSelection = useMemo(() => buildSelectionState(match), [match]);
  const initialNotes = useMemo(() => buildNotesState(match), [match]);
  const selectionDirty = selectionKey(selection) !== selectionKey(initialSelection);
  const notesDirty = JSON.stringify(notes) !== JSON.stringify(initialNotes);
  const dirty = selectionDirty || notesDirty;

  // Név a keretben lévőkhöz akkor is kell, ha már nem aktív játékosok
  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    match.squad.forEach((s) => map.set(s.playerId, s.playerName ?? '?'));
    selectablePlayers.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [match.squad, selectablePlayers]);

  // A lista: a korosztály összes aktív játékosa + aki már nevezve van; kereséskor a teljes klub
  const rows = useMemo<SelectionRowPlayer[]>(() => {
    const term = search.trim().toLocaleLowerCase('hu');
    const byId = new Map<number, SelectionRowPlayer>();
    const otherLabel = (p: SelectablePlayer) =>
      p.ageGroups.includes(event.ageGroup) ? undefined : p.ageGroups.join(', ') || 'Nincs korosztály';

    selectablePlayers.forEach((p) => {
      const inAgeGroup = p.ageGroups.includes(event.ageGroup);
      const matchesSearch = term !== '' && p.name.toLocaleLowerCase('hu').includes(term);
      if ((inAgeGroup && (term === '' || matchesSearch)) || matchesSearch || selection[p.id]) {
        byId.set(p.id, { id: p.id, name: p.name, otherAgeGroup: otherLabel(p) });
      }
    });
    Object.keys(selection).map(Number).forEach((id) => {
      if (!byId.has(id)) {
        const name = nameById.get(id) ?? '?';
        if (term === '' || name.toLocaleLowerCase('hu').includes(term)) {
          byId.set(id, { id, name });
        }
      }
    });
    if (term !== '') {
      // Kereséskor a nevezettek is szűrődnek
      Array.from(byId.values()).forEach((r) => {
        if (!r.name.toLocaleLowerCase('hu').includes(term)) byId.delete(r.id);
      });
    }
    // Saját korosztály elöl, a felhozottak utána – névsorban
    return Array.from(byId.values()).sort((a, b) => {
      if (!!a.otherAgeGroup !== !!b.otherAgeGroup) return a.otherAgeGroup ? 1 : -1;
      return a.name.localeCompare(b.name, 'hu');
    });
  }, [selectablePlayers, selection, nameById, search, event.ageGroup]);

  const starters = Object.values(selection).filter((s) => s.role === MatchSelectionRole.Starter).length;
  const subs = Object.values(selection).filter((s) => s.role === MatchSelectionRole.Sub).length;

  const shirtCounts = new Map<number, number>();
  Object.values(selection).forEach((s) => {
    if (s.shirtNumber != null) shirtCounts.set(s.shirtNumber, (shirtCounts.get(s.shirtNumber) ?? 0) + 1);
  });
  const isShirtDuplicate = (n?: number) => n != null && (shirtCounts.get(n) ?? 0) > 1;
  const hasDuplicateShirts = Array.from(shirtCounts.values()).some((c) => c > 1);

  // Mezszám-javaslatok: csak az üres mezszámú nevezetteknek, a már foglalt számok kihagyásával
  const takenShirts = new Set(shirtCounts.keys());
  const fillableShirts = fillShirts(
    Object.keys(selection).map(Number).filter((id) => selection[id].shirtNumber == null),
    shirtSuggestions,
    takenShirts
  );
  const handleFillShirts = () => {
    setSelection((prev) => {
      const next = { ...prev };
      fillableShirts.forEach((number, playerId) => {
        if (next[playerId]) next[playerId] = { ...next[playerId], shirtNumber: number };
      });
      return next;
    });
  };

  const setRole = (playerId: number, role: MatchSelectionRole | null) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (role == null) delete next[playerId];
      else next[playerId] = { ...prev[playerId], role };
      return next;
    });
  };

  const updateEntry = (playerId: number, patch: Partial<SelectionEntry>) => {
    setSelection((prev) => (prev[playerId] ? { ...prev, [playerId]: { ...prev[playerId], ...patch } } : prev));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      let updated: Match | null = null;
      if (notesDirty) {
        // Üres szöveget küldünk, hogy egy mező törölhető is legyen
        updated = await matchesAPI.update(match.id, notes);
      }
      if (selectionDirty) {
        // A cserék sorrendje a lista sorrendje
        const ordered = rows.map((r) => r.id).filter((id) => selection[id]);
        Object.keys(selection).map(Number).forEach((id) => { if (!ordered.includes(id)) ordered.push(id); });
        updated = await matchesAPI.setSelection(match.id, {
          formation: match.formation,
          players: ordered.map((id) => {
            const starter = selection[id].role === MatchSelectionRole.Starter;
            return {
              playerId: id,
              role: selection[id].role,
              shirtNumber: selection[id].shirtNumber,
              position: selection[id].position,
              pitchX: starter ? selection[id].pitchX : undefined,
              pitchY: starter ? selection[id].pitchY : undefined,
            };
          }),
        });
      }
      if (updated) onSaved(updated);
      setSavedAt(Date.now());
      setEditing(false);
    } catch (err) {
      console.error(err);
      setSaveError(getApiErrorMessage(err, 'Nem sikerült menteni a nevezést.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelection(initialSelection);
    setNotes(initialNotes);
    setSaveError(null);
    setEditing(false);
  };

  if (!canEdit || !editing) {
    const lineup = match.lineup;
    // Régi adatnál a csak keretben szereplők is a cserék közé kerülnek
    const starterIds = new Set(match.lineup.map((l) => l.playerId));
    const subList = [
      ...[...match.subs].sort((a, b) => a.subOrder - b.subOrder),
      ...match.squad.filter((sq) => !starterIds.has(sq.playerId) && !match.subs.some((s) => s.playerId === sq.playerId)),
    ];
    const noteItems: [string, string | undefined][] = [
      ['Ellenfél', match.opponentName],
      ['Ellenfél – megjegyzések', match.opponentNotes],
      ['Pontrúgás – támadó', match.setPieceAttackNotes],
      ['Pontrúgás – védekező', match.setPieceDefenseNotes],
      ['Egyéb', match.otherNotes],
    ];
    const { formation, slots } = slotsFromLineup(
      matchFormation(match),
      lineup.map((l) => ({ playerId: l.playerId, position: l.position, pitchX: l.pitchX, pitchY: l.pitchY }))
    );
    const pitchPlayers = new Map<number, PitchPlayer>(
      lineup.map((l) => [l.playerId, { id: l.playerId, name: l.playerName ?? '?', shirtNumber: l.shirtNumber }])
    );
    const hasSelection = lineup.length + subList.length > 0;
    const subPosition = (playerId: number) => match.subs.find((sb) => sb.playerId === playerId)?.position;
    const openEditor = () => setLineupEditorOpen(true);

    return (
      <div className="space-y-6">
        {lineupEditorOpen && (
          <LineupEditor
            match={match}
            eventTitle={event.title}
            eventAgeGroup={event.ageGroup}
            selectablePlayers={selectablePlayers}
            responses={responses}
            shirtSuggestions={shirtSuggestions}
            onSaved={(updated) => {
              onSaved(updated);
              setSavedAt(Date.now());
            }}
            onClose={() => setLineupEditorOpen(false)}
          />
        )}

        {hasSelection && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
              {lineup.length > 0 && (
                <span className="rounded-full bg-gray-900 px-2.5 py-0.5 text-sm font-semibold tabular-nums text-white">{formation}</span>
              )}
              <span>
                <span className="font-semibold text-gray-900">{lineup.length + subList.length}</span> fő ·{' '}
                <span className="font-semibold text-green-700">{lineup.length}</span> kezdő ·{' '}
                <span className="font-semibold text-blue-700">{subList.length}</span> csere
              </span>
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4" />
                  Lista
                </button>
                <button
                  type="button"
                  onClick={openEditor}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  <UserGroupIcon className="h-4 w-4" />
                  Felállás szerkesztése
                </button>
              </div>
            )}
          </div>
        )}

        {!hasSelection ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 px-4 py-8 text-center">
            <UserGroupIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm font-medium text-gray-900">Még nincs nevezés</p>
            <p className="mt-1 text-sm text-gray-500">
              {canEdit ? 'Állítsd össze a kezdőt a pályán, vagy jelöld ki a keretet egy listában.' : 'A stáb még nem adta meg a meccs keretét.'}
            </p>
            {canEdit && (
              <div className="mt-4 flex flex-col justify-center gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={openEditor}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  <UserGroupIcon className="h-4 w-4" />
                  Felállás összeállítása
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <PencilIcon className="h-4 w-4" />
                  Nevezés listában
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            <div className="mx-auto w-full max-w-[340px] shrink-0 md:mx-0 md:w-[300px]">
              {lineup.length > 0 ? (
                canEdit ? (
                  <button type="button" onClick={openEditor} className="group relative block w-full rounded-xl text-left" aria-label="Felállás szerkesztése">
                    <FormationPitch slots={slots} players={pitchPlayers} tokenSize="sm" />
                    <span className="pointer-events-none absolute inset-x-0 bottom-3 mx-auto w-max rounded-full bg-gray-900/80 px-3 py-1 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
                      Szerkesztés
                    </span>
                  </button>
                ) : (
                  <FormationPitch slots={slots} players={pitchPlayers} tokenSize="sm" />
                )
              ) : (
                <p className="rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">Még nincs kezdő megadva.</p>
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-6">
              <div>
                <h4 className="mb-2 text-sm font-semibold text-gray-900">Cserék</h4>
                {subList.length === 0 ? (
                  <p className="text-sm text-gray-500">Nincs csere megadva.</p>
                ) : (
                  <ul className="space-y-1">
                    {subList.map((sub) => (
                      <li key={sub.playerId} className="flex items-center gap-2 text-sm">
                        <Jersey number={sub.shirtNumber} keeper={subPosition(sub.playerId) === 'GK'} className="h-7 w-7 shrink-0" />
                        <span className="text-gray-900">{sub.playerName}</span>
                        {subPosition(sub.playerId) && (
                          <span className="rounded bg-gray-900 px-1 text-[10px] font-bold text-white">{subPosition(sub.playerId)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {noteItems.some(([, v]) => v) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Felkészülés</h4>
                  {noteItems.filter(([, v]) => v).map(([label, value]) => (
                    <div key={label}>
                      <div className="text-xs font-medium text-gray-500">{label}</div>
                      <p className="whitespace-pre-line text-sm text-gray-800">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const roleButton = (playerId: number, role: MatchSelectionRole | null, label: string, activeClass: string) => {
    const current = selection[playerId]?.role ?? null;
    const active = current === role;
    return (
      <button
        type="button"
        onClick={() => setRole(playerId, role)}
        className={`px-2.5 py-1 text-xs font-medium border first:rounded-l last:rounded-r -ml-px first:ml-0 ${
          active ? activeClass : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
        }`}
        aria-pressed={active}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Nevezés</h4>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            {fillableShirts.size > 0 && (
              <button
                type="button"
                onClick={handleFillShirts}
                className="rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50"
                title="Az üres mezszámok kitöltése a korosztály korábbi meccsei alapján"
              >
                # Mezszámok kitöltése ({fillableShirts.size})
              </button>
            )}
            <span>
              <span className="font-semibold text-green-700">{starters}</span> kezdő ·{' '}
              <span className="font-semibold text-blue-700">{subs}</span> csere
            </span>
          </div>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés a klub összes játékosa között…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-base sm:text-sm"
          />
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            {search.trim() ? 'Nincs találat.' : 'Ebben a korosztályban nincs aktív játékos – keress név szerint.'}
          </p>
        ) : (
          <ul className="divide-y border rounded-md">
            {rows.map((p) => {
              const entry = selection[p.id];
              const response = responses.get(p.id);
              const badge = response != null ? RESPONSE_BADGES[response] : undefined;
              return (
                <li
                  key={p.id}
                  className={`px-3 py-2 flex flex-col gap-2 sm:flex-row sm:items-center ${
                    entry?.role === MatchSelectionRole.Starter ? 'bg-green-50/40' : entry ? 'bg-blue-50/40' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className={`text-sm truncate ${entry ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{p.name}</span>
                    {p.otherAgeGroup && (
                      <span className="shrink-0 px-1.5 py-0.5 text-[11px] rounded bg-gray-100 text-gray-600">{p.otherAgeGroup}</span>
                    )}
                    {badge && (
                      <span className={`shrink-0 px-1.5 py-0.5 text-[11px] rounded border ${badge.className}`}>{badge.label}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex">
                      {roleButton(p.id, MatchSelectionRole.Starter, 'Kezdő', 'bg-green-600 text-white border-green-600')}
                      {roleButton(p.id, MatchSelectionRole.Sub, 'Csere', 'bg-blue-600 text-white border-blue-600')}
                      {roleButton(p.id, null, '–', 'bg-gray-200 text-gray-700 border-gray-300')}
                    </div>
                    {entry && (
                      <input
                        type="number"
                        min={1}
                        max={99}
                        placeholder="#"
                        aria-label="Mezszám"
                        value={entry.shirtNumber ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateEntry(p.id, { shirtNumber: v === '' ? undefined : Math.min(99, Math.max(1, parseInt(v, 10) || 1)) });
                        }}
                        className={`w-14 px-2 py-1 border rounded text-base sm:text-sm text-center ${
                          isShirtDuplicate(entry.shirtNumber) ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                        title={isShirtDuplicate(entry.shirtNumber) ? 'Ez a mezszám már foglalt!' : 'Mezszám'}
                      />
                    )}
                    {entry && entry.shirtNumber == null && suggestShirt(p.id, shirtSuggestions, takenShirts) != null && (
                      <button
                        type="button"
                        onClick={() => updateEntry(p.id, { shirtNumber: suggestShirt(p.id, shirtSuggestions, takenShirts) })}
                        className="rounded border border-dashed border-gray-400 px-1.5 py-0.5 text-xs text-gray-500 hover:border-primary-400 hover:text-primary-700"
                        title="Ezt a mezszámot viselte a legutóbbi meccseken – koppints a beírásához"
                      >
                        #{suggestShirt(p.id, shirtSuggestions, takenShirts)}
                      </button>
                    )}
                    {entry && (
                      <input
                        type="text"
                        placeholder={entry.role === MatchSelectionRole.Starter ? 'Poszt' : 'Poszt (pl. GK)'}
                        aria-label="Poszt"
                        value={entry.position ?? ''}
                        onChange={(e) => updateEntry(p.id, { position: e.target.value || undefined })}
                        className="w-28 px-2 py-1 border border-gray-300 rounded text-base sm:text-sm"
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Felkészülés</h4>
        <NotesField label="Ellenfél" value={notes.opponentName} onChange={(v) => setNotes((n) => ({ ...n, opponentName: v }))} singleLine />
        <NotesField label="Ellenfél – megjegyzések" value={notes.opponentNotes} onChange={(v) => setNotes((n) => ({ ...n, opponentNotes: v }))} rows={3} />
        <NotesField label="Pontrúgás – támadó" value={notes.setPieceAttackNotes} onChange={(v) => setNotes((n) => ({ ...n, setPieceAttackNotes: v }))} />
        <NotesField label="Pontrúgás – védekező" value={notes.setPieceDefenseNotes} onChange={(v) => setNotes((n) => ({ ...n, setPieceDefenseNotes: v }))} />
        <NotesField label="Egyéb" value={notes.otherNotes} onChange={(v) => setNotes((n) => ({ ...n, otherNotes: v }))} />
      </div>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={saveError ?? (hasDuplicateShirts ? 'Egy mezszám csak egyszer szerepelhet.' : null)}
        disabled={hasDuplicateShirts}
        savedAt={savedAt}
        label="Nevezés mentése"
        onSave={handleSave}
        onCancel={match.squad.length > 0 ? handleCancel : undefined}
      />
    </div>
  );
};

const NotesField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  singleLine?: boolean;
}> = ({ label, value, onChange, rows = 2, singleLine }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {singleLine ? (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    ) : (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    )}
  </div>
);

// Alsó, ragadós mentés sáv: egy fülön egy mentés gomb, egyértelmű állapottal
const SaveBar: React.FC<{
  dirty: boolean;
  saving: boolean;
  error: string | null;
  disabled?: boolean;
  savedAt: number | null;
  label: string;
  onSave: () => void;
  onCancel?: () => void;
}> = ({ dirty, saving, error, disabled, savedAt, label, onSave, onCancel }) => (
  <div className="sticky bottom-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-white border-t flex items-center justify-end gap-3">
    <span className={`text-sm mr-auto ${error ? 'text-red-600' : 'text-gray-500'}`}>
      {error ?? (dirty ? 'Nem mentett változások' : savedAt ? 'Mentve' : '')}
    </span>
    {onCancel && (
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
      >
        Mégse
      </button>
    )}
    <button
      type="button"
      onClick={onSave}
      disabled={!dirty || saving || disabled}
      className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {saving ? 'Mentés…' : label}
    </button>
  </div>
);

// --- Eredmény tab: végeredmény + játékosonként perc, gól, gólpassz, lapok ---
interface ResultRow {
  playerId: number;
  name: string;
  shirtNumber?: number;
  isStarter: boolean;
}

interface ResultEntry extends PlayerIncidentCounts {
  minutes: string;
}

const getMatchLength = (event: Event): number => {
  const diff = Math.round((new Date(event.endTime).getTime() - new Date(event.startTime).getTime()) / 60000);
  return diff > 0 && diff <= 150 ? diff : 90;
};

const buildResultRows = (match: Match): ResultRow[] => {
  const rows: ResultRow[] = [];
  const seen = new Set<number>();
  const add = (playerId: number, name: string | undefined, shirtNumber: number | undefined, isStarter: boolean) => {
    if (seen.has(playerId)) return;
    seen.add(playerId);
    rows.push({ playerId, name: name ?? '?', shirtNumber, isStarter });
  };
  match.lineup.forEach((l) => add(l.playerId, l.playerName, l.shirtNumber, true));
  [...match.subs].sort((a, b) => a.subOrder - b.subOrder).forEach((s) => add(s.playerId, s.playerName, s.shirtNumber, false));
  match.squad.forEach((s) => add(s.playerId, s.playerName, s.shirtNumber, false));
  // Régi adat: van perc/esemény, de a játékos már nincs a keretben – így sem vész el
  match.minutesPlayed.forEach((m) => add(m.playerId, m.playerName, undefined, false));
  match.incidents.forEach((i) => add(i.playerId, i.playerName, undefined, false));
  return rows;
};

const buildResultState = (match: Match, rows: ResultRow[], matchLength: number): Record<number, ResultEntry> => {
  const counts = countIncidents(match);
  const minutes = new Map(match.minutesPlayed.map((m) => [m.playerId, m.minutesPlayed]));
  const hasSavedMinutes = match.minutesPlayed.length > 0;
  const state: Record<number, ResultEntry> = {};
  rows.forEach((r) => {
    const c = counts.get(r.playerId) ?? { goals: 0, assists: 0, yellowCards: 0, redCards: 0 };
    const saved = minutes.get(r.playerId);
    // Ha még senkinek nincs perce rögzítve, a kezdők a teljes meccset kapják alapból
    const minutesValue = saved != null ? String(saved) : !hasSavedMinutes && r.isStarter ? String(matchLength) : '';
    state[r.playerId] = { ...c, minutes: minutesValue };
  });
  return state;
};

const COUNTER_COLUMNS: { key: keyof PlayerIncidentCounts; label: string; short: string }[] = [
  { key: 'goals', label: 'Gól', short: 'Gól' },
  { key: 'assists', label: 'Gólpassz', short: 'Gólp.' },
  { key: 'yellowCards', label: 'Sárga', short: 'Sárga' },
  { key: 'redCards', label: 'Piros', short: 'Piros' },
];

const Stepper: React.FC<{ value: number; onChange: (v: number) => void; label: string }> = ({ value, onChange, label }) => (
  <div className="inline-flex items-center border border-gray-300 rounded">
    <button
      type="button"
      onClick={() => onChange(Math.max(0, value - 1))}
      disabled={value === 0}
      className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30"
      aria-label={`${label} csökkentése`}
    >
      <MinusIcon className="h-3.5 w-3.5" />
    </button>
    <span className={`w-6 text-center text-sm tabular-nums ${value > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>{value}</span>
    <button
      type="button"
      onClick={() => onChange(value + 1)}
      className="p-1.5 text-gray-600 hover:bg-gray-100"
      aria-label={`${label} növelése`}
    >
      <PlusIcon className="h-3.5 w-3.5" />
    </button>
  </div>
);

interface MatchResultTabProps {
  match: Match;
  event: Event;
  canEdit: boolean;
  onSaved: (match: Match) => void;
  onGoToSelection: () => void;
}

const MatchResultTab: React.FC<MatchResultTabProps> = ({ match, event, canEdit, onSaved, onGoToSelection }) => {
  const rows = useMemo(() => buildResultRows(match), [match]);
  const defaultLength = getMatchLength(event);
  const [matchLength, setMatchLength] = useState<number>(defaultLength);
  const [homeScore, setHomeScore] = useState<string>(match.homeScore != null ? String(match.homeScore) : '');
  const [awayScore, setAwayScore] = useState<string>(match.awayScore != null ? String(match.awayScore) : '');
  const [isHome, setIsHome] = useState<boolean | null>(match.isHomeMatch ?? null);
  const [entries, setEntries] = useState<Record<number, ResultEntry>>(() => buildResultState(match, rows, matchLength));
  const [baseline, setBaseline] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const snapshot = (h: string, a: string, home: boolean | null, e: Record<number, ResultEntry>) =>
    JSON.stringify({ h, a, home, e });

  useEffect(() => {
    const h = match.homeScore != null ? String(match.homeScore) : '';
    const a = match.awayScore != null ? String(match.awayScore) : '';
    const e = buildResultState(match, rows, defaultLength);
    const home = match.isHomeMatch ?? null;
    setHomeScore(h);
    setAwayScore(a);
    setIsHome(home);
    setEntries(e);
    setMatchLength(defaultLength);
    // Az alapértelmezett kezdő-percek még nincsenek elmentve, ezért azokkal együtt is menthető
    setBaseline(match.minutesPlayed.length > 0 || !rows.some((r) => r.isStarter) ? snapshot(h, a, home, e) : '');
  }, [match, rows, defaultLength]);

  const dirty = snapshot(homeScore, awayScore, isHome, entries) !== baseline;

  const updateEntry = (playerId: number, patch: Partial<ResultEntry>) =>
    setEntries((prev) => ({ ...prev, [playerId]: { ...prev[playerId], ...patch } }));

  // A meccshossz átírásakor azok a kezdők is követik, akiknél még az előző alapérték áll
  const handleMatchLengthChange = (value: string) => {
    const next = Math.max(1, Math.min(150, parseInt(value, 10) || 0));
    if (!value || isNaN(next)) return;
    setEntries((prev) => {
      const updated = { ...prev };
      rows.forEach((r) => {
        if (r.isStarter && updated[r.playerId]?.minutes === String(matchLength)) {
          updated[r.playerId] = { ...updated[r.playerId], minutes: String(next) };
        }
      });
      return updated;
    });
    setMatchLength(next);
  };

  const handleSave = async () => {
    const parseScore = (v: string) => (v === '' ? undefined : parseInt(v, 10));
    const home = parseScore(homeScore);
    const away = parseScore(awayScore);
    if ((home !== undefined && (isNaN(home) || home < 0)) || (away !== undefined && (isNaN(away) || away < 0))) {
      setSaveError('Érvénytelen eredmény.');
      return;
    }
    const players: MatchPlayerResultDto[] = rows.map((r) => {
      const e = entries[r.playerId];
      const minutes = e.minutes === '' ? undefined : Math.max(0, parseInt(e.minutes, 10) || 0);
      return {
        playerId: r.playerId,
        minutesPlayed: minutes,
        goals: e.goals,
        assists: e.assists,
        yellowCards: e.yellowCards,
        redCards: e.redCards,
      };
    });
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await matchesAPI.setResult(match.id, {
        homeScore: home,
        awayScore: away,
        isHomeMatch: isHome ?? undefined,
        players,
      });
      onSaved(updated);
      setSavedAt(Date.now());
    } catch (err) {
      console.error(err);
      setSaveError(getApiErrorMessage(err, 'Nem sikerült menteni az eredményt.'));
    } finally {
      setSaving(false);
    }
  };

  const scoreInput = (value: string, onChange: (v: string) => void, label: string) => (
    <div className="flex flex-col items-center gap-1">
      <input
        type="number"
        min={0}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!canEdit}
        aria-label={label}
        className="w-16 px-2 py-2 border border-gray-300 rounded text-center text-lg font-semibold disabled:bg-gray-50"
      />
      <span className="text-[11px] text-gray-500">{label}</span>
    </div>
  );

  // Melyik oldal vagyunk mi: a valós sorrend marad (hazai – vendég), csak megjelöljük
  const scoreLabel = (side: string, isUs: boolean) =>
    isHome == null ? side : `${side} · ${isUs ? 'mi' : match.opponentName || 'ellenfél'}`;

  const parsedScore = (v: string) => {
    const n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  };
  const outcome = getMatchOutcome({
    homeScore: parsedScore(homeScore),
    awayScore: parsedScore(awayScore),
    isHomeMatch: isHome,
  });

  const minutesInput = (r: ResultRow) => (
    <input
      type="number"
      min={0}
      inputMode="numeric"
      placeholder="–"
      aria-label={`${r.name} perc`}
      value={entries[r.playerId]?.minutes ?? ''}
      onChange={(e) => updateEntry(r.playerId, { minutes: e.target.value })}
      className="w-16 px-2 py-1 border border-gray-300 rounded text-base sm:text-sm text-center"
    />
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-6">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Végeredmény</h4>
          <div className="mb-3 inline-flex rounded-md border border-gray-300 p-0.5" role="group" aria-label="Hol játszottunk">
            {([
              [true, 'Hazai pályán'],
              [false, 'Idegenben'],
            ] as const).map(([value, label]) => (
              <button
                key={label}
                type="button"
                onClick={() => setIsHome(value)}
                disabled={!canEdit}
                aria-pressed={isHome === value}
                className={`rounded px-3 py-1.5 text-sm font-medium ${
                  isHome === value ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-start gap-2">
            {scoreInput(homeScore, setHomeScore, scoreLabel('Hazai', isHome === true))}
            <span className="text-gray-400 text-lg mt-2">–</span>
            {scoreInput(awayScore, setAwayScore, scoreLabel('Vendég', isHome === false))}
            {outcome && (
              <span className={`ml-2 mt-2.5 text-sm font-semibold ${OUTCOME_LABELS[outcome].className}`}>
                {OUTCOME_LABELS[outcome].text}
              </span>
            )}
          </div>
          {isHome == null && (
            <p className="mt-2 text-xs text-amber-700">
              Állítsd be, hogy hazai pályán vagy idegenben játszottunk - ebből tudjuk, győzelem vagy vereség-e az eredmény.
            </p>
          )}
        </div>
        {canEdit && rows.some((r) => r.isStarter) && (
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Meccshossz</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={150}
                value={matchLength}
                onChange={(e) => handleMatchLengthChange(e.target.value)}
                className="w-20 px-2 py-2 border border-gray-300 rounded text-center"
              />
              <span className="text-sm text-gray-500">perc</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Játékosok</h4>
        {rows.length === 0 ? (
          <div className="text-sm text-gray-500">
            Még nincs nevezett játékos.{' '}
            {canEdit && (
              <button type="button" onClick={onGoToSelection} className="text-primary-600 hover:underline">
                Nevezés megadása
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobil kártyák (md alatt) */}
            <ul className="md:hidden space-y-2">
              {rows.map((r) => {
                const e = entries[r.playerId];
                if (!e) return null;
                return (
                  <li key={r.playerId} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {r.shirtNumber != null && <span className="text-gray-500 mr-1">#{r.shirtNumber}</span>}
                          {r.name}
                        </div>
                        <div className="text-[11px] text-gray-500">{r.isStarter ? 'Kezdő' : 'Csere'}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {canEdit ? minutesInput(r) : <span className="text-sm font-semibold">{e.minutes || '–'}</span>}
                        <span className="text-xs text-gray-500">perc</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {COUNTER_COLUMNS.map((c) => (
                        <div key={c.key} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-600">{c.label}</span>
                          {canEdit ? (
                            <Stepper value={e[c.key]} onChange={(v) => updateEntry(r.playerId, { [c.key]: v })} label={c.label} />
                          ) : (
                            <span className="text-sm font-semibold">{e[c.key]}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
            {/* Desktop táblázat (md-től) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="text-left py-2 font-medium">Játékos</th>
                    <th className="text-center py-2 font-medium">Perc</th>
                    {COUNTER_COLUMNS.map((c) => (
                      <th key={c.key} className="text-center py-2 font-medium">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const e = entries[r.playerId];
                    if (!e) return null;
                    const firstSub = !r.isStarter && idx > 0 && rows[idx - 1].isStarter;
                    return (
                      <tr key={r.playerId} className={`border-b border-gray-100 ${firstSub ? 'border-t-2 border-t-gray-200' : ''}`}>
                        <td className="py-1.5 pr-2">
                          {r.shirtNumber != null && <span className="text-gray-500 mr-1">#{r.shirtNumber}</span>}
                          {r.name}
                          {!r.isStarter && <span className="ml-2 text-[11px] text-gray-400">csere</span>}
                        </td>
                        <td className="text-center py-1.5">{canEdit ? minutesInput(r) : e.minutes || '–'}</td>
                        {COUNTER_COLUMNS.map((c) => (
                          <td key={c.key} className="text-center py-1.5">
                            {canEdit ? (
                              <Stepper value={e[c.key]} onChange={(v) => updateEntry(r.playerId, { [c.key]: v })} label={c.label} />
                            ) : (
                              e[c.key]
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {canEdit && (
        <SaveBar
          dirty={dirty}
          saving={saving}
          error={saveError}
          savedAt={savedAt}
          label="Eredmény mentése"
          onSave={handleSave}
        />
      )}
    </div>
  );
};

// --- Statisztika tab: meccs összesítő + korosztály összesítő ---
interface MatchStatsTabProps {
  match: Match;
  event: Event;
}

// Mobil statisztika-kártya cella: címke + érték
const StatCell: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div>
    <div className="text-[11px] text-gray-400">{label}</div>
    <div className="text-sm font-semibold text-gray-900">{value}</div>
  </div>
);

const MatchStatsTab: React.FC<MatchStatsTabProps> = ({ match, event }) => {
  const [aggregateStats, setAggregateStats] = useState<import('../../types/match').PlayerMatchStatsDto[]>([]);
  const [aggregateLoading, setAggregateLoading] = useState(false);

  useEffect(() => {
    if (!event.ageGroupId) return;
    setAggregateLoading(true);
    matchesAPI
      .getStatistics({ ageGroupId: event.ageGroupId })
      .then(setAggregateStats)
      .catch(() => setAggregateStats([]))
      .finally(() => setAggregateLoading(false));
  }, [event.ageGroupId]);

  const minutesMap = new Map(match.minutesPlayed.map((m) => [m.playerId, m.minutesPlayed]));
  const counts = countIncidents(match);
  const countOf = (playerId: number, key: keyof PlayerIncidentCounts) => counts.get(playerId)?.[key] ?? 0;

  const squadBatch = useBatchedList(match.squad, 5);
  const aggregateBatch = useBatchedList(aggregateStats, 5);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Ez a mérkőzés</h4>
        {/* Mobil kártyák (md alatt) */}
        <div className="md:hidden grid grid-cols-2 gap-3">
          {match.squad.length === 0 ? (
            <p className="col-span-2 text-sm text-gray-500">Nincs játékos a keretben.</p>
          ) : (
            <>
              {squadBatch.visible.map((s) => (
                <div key={s.id} className="bg-white rounded-lg shadow border p-3 flex flex-col">
                  <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug mb-1.5">{s.playerName}</div>
                  <div className="grid grid-cols-3 gap-1 text-center">
                    <StatCell label="Perc" value={minutesMap.get(s.playerId) ?? 0} />
                    <StatCell label="Gól" value={countOf(s.playerId, 'goals')} />
                    <StatCell label="Gólpassz" value={countOf(s.playerId, 'assists')} />
                    <StatCell label="Sárga" value={countOf(s.playerId, 'yellowCards')} />
                    <StatCell label="Piros" value={countOf(s.playerId, 'redCards')} />
                  </div>
                </div>
              ))}
              {squadBatch.hasMore && (
                <div className="col-span-2">
                  <LazyLoadTrigger key={squadBatch.visible.length} onLoadMore={squadBatch.loadMore} />
                </div>
              )}
            </>
          )}
        </div>
        {/* Desktop táblázat (md-től) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Játékos</th>
                <th className="text-right py-2">Perc</th>
                <th className="text-right py-2">Gól</th>
                <th className="text-right py-2">Gólpassz</th>
                <th className="text-right py-2">Sárga</th>
                <th className="text-right py-2">Piros</th>
              </tr>
            </thead>
            <tbody>
              {match.squad.map((s) => (
                <tr key={s.id} className="border-b border-gray-100">
                  <td className="py-1">{s.playerName}</td>
                  <td className="text-right">{minutesMap.get(s.playerId) ?? 0}</td>
                  <td className="text-right">{countOf(s.playerId, 'goals')}</td>
                  <td className="text-right">{countOf(s.playerId, 'assists')}</td>
                  <td className="text-right">{countOf(s.playerId, 'yellowCards')}</td>
                  <td className="text-right">{countOf(s.playerId, 'redCards')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {event.ageGroupId && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Korosztály összesítő (összes mérkőzés)</h4>
          {aggregateLoading ? (
            <p className="text-sm text-gray-500">Betöltés...</p>
          ) : aggregateStats.length === 0 ? (
            <p className="text-sm text-gray-500">Nincs statisztika.</p>
          ) : (
            <>
              {/* Mobil kártyák (md alatt) */}
              <div className="md:hidden grid grid-cols-2 gap-3">
                {aggregateBatch.visible.map((row) => (
                  <div key={row.playerId} className="bg-white rounded-lg shadow border p-3 flex flex-col">
                    <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug mb-1.5">{row.playerName}</div>
                    <div className="grid grid-cols-3 gap-1 text-center">
                      <StatCell label="Meccs" value={row.matchesPlayed} />
                      <StatCell label="Perc" value={row.totalMinutes} />
                      <StatCell label="Gól" value={row.goals} />
                      <StatCell label="Gólp." value={row.assists} />
                      <StatCell label="Sárga" value={row.yellowCards} />
                      <StatCell label="Piros" value={row.redCards} />
                    </div>
                  </div>
                ))}
                {aggregateBatch.hasMore && (
                  <div className="col-span-2">
                    <LazyLoadTrigger key={aggregateBatch.visible.length} onLoadMore={aggregateBatch.loadMore} />
                  </div>
                )}
              </div>
              {/* Desktop táblázat (md-től) */}
              <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Játékos</th>
                    <th className="text-right py-2">Meccs</th>
                    <th className="text-right py-2">Perc</th>
                    <th className="text-right py-2">Gól</th>
                    <th className="text-right py-2">Gólpassz</th>
                    <th className="text-right py-2">Sárga</th>
                    <th className="text-right py-2">Piros</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregateStats.map((row) => (
                    <tr key={row.playerId} className="border-b border-gray-100">
                      <td className="py-1">{row.playerName}</td>
                      <td className="text-right">{row.matchesPlayed}</td>
                      <td className="text-right">{row.totalMinutes}</td>
                      <td className="text-right">{row.goals}</td>
                      <td className="text-right">{row.assists}</td>
                      <td className="text-right">{row.yellowCards}</td>
                      <td className="text-right">{row.redCards}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchDetailView;
